import React, { useState } from 'react';
import { 
  Palette, 
  Type, 
  Image, 
  Settings, 
  Eye, 
  Download, 
  Upload,
  RotateCcw,
  Save,
  Copy,
  Check,
  Users,
  Briefcase
} from 'lucide-react';
import { useBranding, BrandingConfig } from '../../contexts/BrandingContext';
import { BrandedHeader, BrandedButton, BrandedCard, BrandedInput, BrandedSelect, BrandedBadge, BrandedAlert } from './BrandedComponents';

interface BrandingCustomizerProps {
  onClose?: () => void;
  showPreview?: boolean;
}

export const BrandingCustomizer: React.FC<BrandingCustomizerProps> = ({
  onClose,
  showPreview = true
}) => {
  const { branding, setBranding, resetBranding, applyPreset, getCSSVariables, saveBranding, isLoading, hasCustomBranding, lastSaved } = useBranding();
  const [activeTab, setActiveTab] = useState<'company' | 'colors' | 'typography' | 'layout' | 'export'>('company');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [urlCopied, setUrlCopied] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [previewMode, setPreviewMode] = useState<'client' | 'attorney'>('client');

  // Handle logo upload
  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setBranding({
          companyLogo: e.target?.result as string
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle save branding
  const handleSave = async () => {
    try {
      await saveBranding();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save branding:', error);
      // Could add error toast here
    }
  };

  // Generate shareable URL with branding parameters
  const generateShareableUrl = () => {
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams();
    
    params.set('companyName', branding.companyName);
    params.set('primaryColor', branding.colors.primary);
    params.set('secondaryColor', branding.colors.secondary);
    if (branding.tagline) params.set('tagline', branding.tagline);
    if (branding.companyLogo) params.set('logo', branding.companyLogo);
    
    currentUrl.search = params.toString();
    return currentUrl.toString();
  };

  // Copy URL to clipboard
  const copyShareableUrl = async () => {
    try {
      await navigator.clipboard.writeText(generateShareableUrl());
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  // Export branding configuration
  const exportBranding = () => {
    const dataStr = JSON.stringify(branding, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `${branding.companyName.toLowerCase().replace(/\s+/g, '-')}-branding.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Import branding configuration
  const importBranding = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedBranding = JSON.parse(e.target?.result as string) as BrandingConfig;
          setBranding(importedBranding);
        } catch (error) {
          console.error('Failed to import branding:', error);
          alert('Invalid branding file. Please check the file format.');
        }
      };
      reader.readAsText(file);
    }
  };

  const tabs = [
    { id: 'company' as const, label: 'Company', icon: Settings },
    { id: 'colors' as const, label: 'Colors', icon: Palette },
    { id: 'typography' as const, label: 'Typography', icon: Type },
    { id: 'layout' as const, label: 'Layout', icon: Settings },
    { id: 'export' as const, label: 'Export', icon: Download },
  ];

  const presetOptions = [
    { value: 'legal', label: 'Legal Firm' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'business', label: 'Business' },
    { value: 'default', label: 'Default' },
  ];

  const renderCompanyTab = () => (
    <div className="space-y-6">
      {/* Preset Selection */}
      <div>
        <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
          Quick Presets
        </label>
        <div className="grid grid-cols-2 gap-2">
          {presetOptions.map(preset => (
            <button
              key={preset.value}
              onClick={() => applyPreset(preset.value as any)}
              className="p-3 text-sm border border-light-border dark:border-dark-border rounded-lg hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary transition-colors text-light-text-primary dark:text-dark-text-primary"
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Company Information */}
      <BrandedInput
        label="Company Name *"
        value={branding.companyName}
        onChange={(e) => setBranding({ companyName: e.target.value })}
        placeholder="Enter your company name"
      />

      <BrandedInput
        label="Tagline"
        value={branding.tagline || ''}
        onChange={(e) => setBranding({ tagline: e.target.value })}
        placeholder="Enter your company tagline"
      />

      {/* Logo Upload */}
      <div>
        <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
          Company Logo
        </label>
        <div className="flex items-center space-x-4">
          <input
            type="file"
            accept="image/*"
            onChange={handleLogoUpload}
            className="hidden"
            id="logo-upload"
          />
          <label
            htmlFor="logo-upload"
            className="flex items-center px-4 py-2 border border-light-border dark:border-dark-border rounded-lg cursor-pointer hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary text-light-text-primary dark:text-dark-text-primary"
          >
            <Upload size={16} className="mr-2" />
            Upload Logo
          </label>
          {branding.companyLogo && (
            <img
              src={branding.companyLogo}
              alt="Logo preview"
              className="h-12 w-auto rounded"
            />
          )}
        </div>
      </div>
    </div>
  );

  const renderColorsTab = () => (
    <div className="space-y-6">
      {/* Color Palette Preview */}
      <div className="bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg p-4 mb-6">
        <h4 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-3">Current Color Palette</h4>
        <div className="grid grid-cols-4 gap-2">
          <div className="text-center">
            <div 
              className="h-16 rounded-lg mb-2 border-2 border-light-border dark:border-dark-border" 
              style={{ backgroundColor: branding.colors.primary }}
            />
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">Primary</p>
          </div>
          <div className="text-center">
            <div 
              className="h-16 rounded-lg mb-2 border-2 border-light-border dark:border-dark-border" 
              style={{ backgroundColor: branding.colors.secondary }}
            />
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">Secondary</p>
          </div>
          <div className="text-center">
            <div 
              className="h-16 rounded-lg mb-2 border-2 border-light-border dark:border-dark-border" 
              style={{ backgroundColor: branding.colors.accent }}
            />
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">Accent</p>
          </div>
          <div className="text-center">
            <div 
              className="h-16 rounded-lg mb-2 border-2 border-light-border dark:border-dark-border" 
              style={{ backgroundColor: branding.colors.background }}
            />
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">Background</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
            Primary Color
            <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary ml-2">Headers, buttons, links</span>
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={branding.colors.primary}
              onChange={(e) => setBranding({
                colors: { ...branding.colors, primary: e.target.value }
              })}
              className="w-12 h-10 rounded border-2 border-light-border dark:border-dark-border cursor-pointer"
            />
            <input
              type="text"
              value={branding.colors.primary}
              onChange={(e) => setBranding({
                colors: { ...branding.colors, primary: e.target.value }
              })}
              className="flex-1 px-3 py-2 border border-light-border dark:border-dark-border bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary rounded font-mono text-sm"
              placeholder="#000000"
            />
          </div>
          {/* Usage examples */}
          <div className="mt-2 flex items-center space-x-3 text-xs">
            <span className="text-light-text-secondary dark:text-dark-text-secondary">Used in:</span>
            <span className="px-2 py-1 rounded text-white" style={{ backgroundColor: branding.colors.primary }}>
              Buttons
            </span>
            <span style={{ color: branding.colors.primary }}>Links</span>
            <span className="px-2 py-1 rounded-full text-white text-xs" style={{ backgroundColor: branding.colors.primary }}>
              Badges
            </span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
            Secondary Color
            <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary ml-2">Secondary buttons, subtle accents</span>
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={branding.colors.secondary}
              onChange={(e) => setBranding({
                colors: { ...branding.colors, secondary: e.target.value }
              })}
              className="w-12 h-10 rounded border-2 border-light-border dark:border-dark-border cursor-pointer"
            />
            <input
              type="text"
              value={branding.colors.secondary}
              onChange={(e) => setBranding({
                colors: { ...branding.colors, secondary: e.target.value }
              })}
              className="flex-1 px-3 py-2 border border-light-border dark:border-dark-border bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary rounded font-mono text-sm"
              placeholder="#000000"
            />
          </div>
          {/* Usage examples */}
          <div className="mt-2 flex items-center space-x-3 text-xs">
            <span className="text-light-text-secondary dark:text-dark-text-secondary">Used in:</span>
            <span className="px-2 py-1 rounded border" style={{ borderColor: branding.colors.secondary, color: branding.colors.secondary }}>
              Outline Button
            </span>
            <span className="px-2 py-1 rounded" style={{ backgroundColor: branding.colors.secondary, opacity: 0.1, color: branding.colors.secondary }}>
              Hover States
            </span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
            Accent Color
            <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary ml-2">Highlights, success states, icons</span>
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={branding.colors.accent}
              onChange={(e) => setBranding({
                colors: { ...branding.colors, accent: e.target.value }
              })}
              className="w-12 h-10 rounded border-2 border-light-border dark:border-dark-border cursor-pointer"
            />
            <input
              type="text"
              value={branding.colors.accent}
              onChange={(e) => setBranding({
                colors: { ...branding.colors, accent: e.target.value }
              })}
              className="flex-1 px-3 py-2 border border-light-border dark:border-dark-border bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary rounded font-mono text-sm"
              placeholder="#000000"
            />
          </div>
          {/* Usage examples */}
          <div className="mt-2 flex items-center space-x-3 text-xs">
            <span className="text-light-text-secondary dark:text-dark-text-secondary">Used in:</span>
            <Check size={16} style={{ color: branding.colors.accent }} />
            <span className="px-2 py-1 rounded-full text-white text-xs" style={{ backgroundColor: branding.colors.accent }}>
              Success
            </span>
            <span style={{ color: branding.colors.accent }}>✓ Checkmarks</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
            Background Color
            <span className="text-xs text-light-text-secondary dark:text-dark-text-secondary ml-2">Page background, light surfaces</span>
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="color"
              value={branding.colors.background}
              onChange={(e) => setBranding({
                colors: { ...branding.colors, background: e.target.value }
              })}
              className="w-12 h-10 rounded border-2 border-light-border dark:border-dark-border cursor-pointer"
            />
            <input
              type="text"
              value={branding.colors.background}
              onChange={(e) => setBranding({
                colors: { ...branding.colors, background: e.target.value }
              })}
              className="flex-1 px-3 py-2 border border-light-border dark:border-dark-border bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary rounded font-mono text-sm"
              placeholder="#FFFFFF"
            />
          </div>
          {/* Usage examples */}
          <div className="mt-2">
            <div className="p-3 rounded border border-light-border dark:border-dark-border" style={{ backgroundColor: branding.colors.background }}>
              <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">Background preview area</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Color Schemes */}
      <div className="mt-6 pt-6 border-t border-light-border dark:border-dark-border">
        <h4 className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-3">Quick Color Schemes</h4>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setBranding({
              colors: {
                primary: '#1e40af',
                secondary: '#3730a3',
                accent: '#10b981',
                background: '#f9fafb',
                surface: '#ffffff',
                text: {
                  primary: '#111827',
                  secondary: '#6b7280',
                  muted: '#9ca3af'
                },
                success: '#10b981',
                warning: '#f59e0b',
                error: '#ef4444'
              }
            })}
            className="p-2 border border-light-border dark:border-dark-border rounded-lg hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary text-sm text-light-text-primary dark:text-dark-text-primary"
          >
            <div className="flex space-x-1 mb-1">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#1e40af' }} />
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#3730a3' }} />
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#10b981' }} />
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f9fafb' }} />
            </div>
            Professional Blue
          </button>
          <button
            onClick={() => setBranding({
              colors: {
                primary: '#dc2626',
                secondary: '#991b1b',
                accent: '#f59e0b',
                background: '#fef2f2',
                surface: '#ffffff',
                text: {
                  primary: '#111827',
                  secondary: '#6b7280',
                  muted: '#9ca3af'
                },
                success: '#10b981',
                warning: '#f59e0b',
                error: '#dc2626'
              }
            })}
            className="p-2 border border-light-border dark:border-dark-border rounded-lg hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary text-sm text-light-text-primary dark:text-dark-text-primary"
          >
            <div className="flex space-x-1 mb-1">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#dc2626' }} />
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#991b1b' }} />
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f59e0b' }} />
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#fef2f2' }} />
            </div>
            Bold Red
          </button>
          <button
            onClick={() => setBranding({
              colors: {
                primary: '#059669',
                secondary: '#047857',
                accent: '#3b82f6',
                background: '#f0fdf4',
                surface: '#ffffff',
                text: {
                  primary: '#111827',
                  secondary: '#6b7280',
                  muted: '#9ca3af'
                },
                success: '#059669',
                warning: '#f59e0b',
                error: '#ef4444'
              }
            })}
            className="p-2 border border-light-border dark:border-dark-border rounded-lg hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary text-sm text-light-text-primary dark:text-dark-text-primary"
          >
            <div className="flex space-x-1 mb-1">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#059669' }} />
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#047857' }} />
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#3b82f6' }} />
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f0fdf4' }} />
            </div>
            Fresh Green
          </button>
          <button
            onClick={() => setBranding({
              colors: {
                primary: '#7c3aed',
                secondary: '#6d28d9',
                accent: '#ec4899',
                background: '#faf5ff',
                surface: '#ffffff',
                text: {
                  primary: '#111827',
                  secondary: '#6b7280',
                  muted: '#9ca3af'
                },
                success: '#10b981',
                warning: '#f59e0b',
                error: '#ef4444'
              }
            })}
            className="p-2 border border-light-border dark:border-dark-border rounded-lg hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary text-sm text-light-text-primary dark:text-dark-text-primary"
          >
            <div className="flex space-x-1 mb-1">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#7c3aed' }} />
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#6d28d9' }} />
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#ec4899' }} />
              <div className="w-4 h-4 rounded" style={{ backgroundColor: '#faf5ff' }} />
            </div>
            Modern Purple
          </button>
        </div>
      </div>
    </div>
  );

  const renderTypographyTab = () => (
    <div className="space-y-6">
      <BrandedSelect
        label="Primary Font"
        value={branding.fonts.primary}
        onChange={(e) => setBranding({
          fonts: { ...branding.fonts, primary: e.target.value }
        })}
        options={[
          { value: 'Inter, system-ui, sans-serif', label: 'Inter' },
          { value: 'Roboto, system-ui, sans-serif', label: 'Roboto' },
          { value: 'Open Sans, system-ui, sans-serif', label: 'Open Sans' },
          { value: 'Lato, system-ui, sans-serif', label: 'Lato' },
          { value: 'Montserrat, system-ui, sans-serif', label: 'Montserrat' },
          { value: 'Poppins, system-ui, sans-serif', label: 'Poppins' },
          { value: 'Georgia, serif', label: 'Georgia' },
          { value: 'Times New Roman, serif', label: 'Times New Roman' },
          { value: 'Arial, sans-serif', label: 'Arial' },
        ]}
      />

      <BrandedSelect
        label="Heading Font"
        value={branding.fonts.headings}
        onChange={(e) => setBranding({
          fonts: { ...branding.fonts, headings: e.target.value }
        })}
        options={[
          { value: 'Inter, system-ui, sans-serif', label: 'Inter' },
          { value: 'Roboto, system-ui, sans-serif', label: 'Roboto' },
          { value: 'Open Sans, system-ui, sans-serif', label: 'Open Sans' },
          { value: 'Lato, system-ui, sans-serif', label: 'Lato' },
          { value: 'Montserrat, system-ui, sans-serif', label: 'Montserrat' },
          { value: 'Poppins, system-ui, sans-serif', label: 'Poppins' },
        ]}
      />
    </div>
  );

  const renderLayoutTab = () => (
    <div className="space-y-6">
      <BrandedSelect
        label="Border Radius"
        value={branding.borderRadius}
        onChange={(e) => setBranding({
          borderRadius: e.target.value as any
        })}
        options={[
          { value: 'none', label: 'None (0px)' },
          { value: 'small', label: 'Small (4px)' },
          { value: 'medium', label: 'Medium (8px)' },
          { value: 'large', label: 'Large (12px)' },
          { value: 'full', label: 'Full (999px)' },
        ]}
      />

      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
          Drop Shadows
        </label>
        <input
          type="checkbox"
          checked={branding.shadows}
          onChange={(e) => setBranding({ shadows: e.target.checked })}
          className="w-4 h-4 text-blue-600 dark:text-blue-400 border-light-border dark:border-dark-border rounded focus:ring-blue-500 dark:focus:ring-blue-400"
        />
      </div>

      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
          Animations
        </label>
        <input
          type="checkbox"
          checked={branding.animations}
          onChange={(e) => setBranding({ animations: e.target.checked })}
          className="w-4 h-4 text-blue-600 dark:text-blue-400 border-light-border dark:border-dark-border rounded focus:ring-blue-500 dark:focus:ring-blue-400"
        />
      </div>
    </div>
  );

  const renderExportTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary mb-4">
          Share & Export
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-light-text-primary dark:text-dark-text-primary mb-2">
              Shareable URL
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={generateShareableUrl()}
                readOnly
                className="flex-1 px-3 py-2 border border-light-border dark:border-dark-border rounded bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-primary dark:text-dark-text-primary text-sm"
              />
              <button
                onClick={copyShareableUrl}
                className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                {urlCopied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-1">
              Share this URL to show your branded template to others
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={exportBranding}
              className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
            >
              <Download size={16} className="mr-2" />
              Export Config
            </button>
            
            <div>
              <input
                type="file"
                accept=".json"
                onChange={importBranding}
                className="hidden"
                id="import-branding"
              />
              <label
                htmlFor="import-branding"
                className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors cursor-pointer w-full"
              >
                <Upload size={16} className="mr-2" />
                Import Config
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-light-bg-secondary dark:bg-dark-bg-secondary flex">
      {/* Customizer Panel */}
      <div className="w-96 bg-light-bg-primary dark:bg-dark-bg-primary shadow-lg overflow-y-auto">
        <div className="p-6 border-b border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-light-text-primary dark:text-dark-text-primary">Branding Customizer</h2>
            {onClose && (
              <button
                onClick={onClose}
                className="text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary"
              >
                ×
              </button>
            )}
          </div>
          
          {/* Status Indicator */}
          {hasCustomBranding && (
            <div className="mt-4 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center text-sm">
                <Check size={14} className="text-green-600 dark:text-green-400 mr-2" />
                <span className="text-green-800 dark:text-green-200">
                  Custom branding saved
                  {lastSaved && (
                    <span className="text-green-600 dark:text-green-400 ml-1">
                      • Last updated {new Date(lastSaved).toLocaleDateString()}
                    </span>
                  )}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b border-light-border dark:border-dark-border">
          <nav className="flex">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-light-text-secondary dark:text-dark-text-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary'
                }`}
              >
                <tab.icon size={16} className="mx-auto mb-1" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'company' && renderCompanyTab()}
          {activeTab === 'colors' && renderColorsTab()}
          {activeTab === 'typography' && renderTypographyTab()}
          {activeTab === 'layout' && renderLayoutTab()}
          {activeTab === 'export' && renderExportTab()}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-light-border dark:border-dark-border bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
          <div className="flex space-x-3">
            <button
              onClick={resetBranding}
              className="flex-1 flex items-center justify-center px-4 py-2 border border-light-border dark:border-dark-border rounded text-light-text-primary dark:text-dark-text-primary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors"
            >
              <RotateCcw size={16} className="mr-2" />
              Reset
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className={`flex-1 flex items-center justify-center px-4 py-2 rounded transition-colors ${
                saveSuccess 
                  ? 'bg-green-600 text-white' 
                  : isLoading 
                    ? 'bg-gray-400 dark:bg-gray-600 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {saveSuccess ? (
                <>
                  <Check size={16} className="mr-2" />
                  Saved!
                </>
              ) : isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Preview Panel */}
      {showPreview && (
        <div className="flex-1 overflow-y-auto bg-light-bg-primary dark:bg-dark-bg-primary">
          {/* Preview Mode Switcher */}
          <div className="sticky top-0 z-10 bg-light-bg-secondary dark:bg-dark-bg-secondary border-b border-light-border dark:border-dark-border">
            <div className="max-w-2xl mx-auto px-8 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-light-text-primary dark:text-dark-text-primary">
                  Live Preview
                </h3>
                <div className="flex rounded-lg overflow-hidden border border-light-border dark:border-dark-border bg-light-bg-primary dark:bg-dark-bg-primary">
                  <button
                    onClick={() => setPreviewMode('client')}
                    className={`px-4 py-2 text-sm font-medium transition-all flex items-center ${
                      previewMode === 'client' 
                        ? 'bg-blue-600 dark:bg-blue-500 text-white' 
                        : 'bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary'
                    }`}
                  >
                    <Users size={14} className="mr-2" />
                    Client View
                  </button>
                  <div className="w-px bg-light-border dark:bg-dark-border" />
                  <button
                    onClick={() => setPreviewMode('attorney')}
                    className={`px-4 py-2 text-sm font-medium transition-all flex items-center ${
                      previewMode === 'attorney' 
                        ? 'bg-blue-600 dark:bg-blue-500 text-white' 
                        : 'bg-light-bg-primary dark:bg-dark-bg-primary text-light-text-primary dark:text-dark-text-primary hover:bg-light-bg-tertiary dark:hover:bg-dark-bg-tertiary'
                    }`}
                  >
                    <Briefcase size={14} className="mr-2" />
                    Attorney View
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="max-w-2xl mx-auto">
              {previewMode === 'client' ? (
                <div className="space-y-8">
                  {/* Client Intake Form Preview */}
                  <BrandedCard>
                    <BrandedHeader size="large" />
                    <h4 className="text-lg font-semibold mb-4" style={{ color: 'var(--brand-text-primary)' }}>
                      Legal Consultation Request
                    </h4>
                    <BrandedInput
                      label="Full Name *"
                      placeholder="Enter your full name"
                    />
                    <BrandedInput
                      label="Email Address *"
                      type="email"
                      placeholder="your.email@example.com"
                    />
                    <BrandedSelect
                      label="Type of Legal Matter *"
                      options={[
                        { value: '', label: 'Select case type' },
                        { value: 'personal_injury', label: 'Personal Injury' },
                        { value: 'family_law', label: 'Family Law' },
                        { value: 'criminal_defense', label: 'Criminal Defense' },
                      ]}
                    />
                    <BrandedButton variant="primary" className="w-full">
                      Request Consultation
                    </BrandedButton>
                  </BrandedCard>

                  {/* Client Waiting Room Preview */}
                  <BrandedCard>
                    <div className="text-center">
                      <h4 className="text-xl font-semibold mb-2" style={{ color: 'var(--brand-text-primary)' }}>
                        Waiting for Attorney
                      </h4>
                      <p style={{ color: 'var(--brand-text-secondary)' }}>
                        Your consultation request has been submitted
                      </p>
                      <div className="mt-4">
                        <BrandedBadge variant="primary">WAITING</BrandedBadge>
                      </div>
                    </div>
                  </BrandedCard>

                  {/* Client Alert Preview */}
                  <BrandedAlert variant="info">
                    Your attorney will review your request and join you shortly.
                  </BrandedAlert>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Attorney Dashboard Header */}
                  <div style={{ backgroundColor: 'var(--brand-primary)' }} className="rounded-lg p-6 text-white">
                    <BrandedHeader size="medium" showTagline={false} customTagline="Attorney Dashboard" />
                  </div>

                  {/* Attorney Consultation Queue */}
                  <BrandedCard>
                    <h4 className="text-lg font-semibold mb-4" style={{ color: 'var(--brand-text-primary)' }}>
                      Pending Consultations
                    </h4>
                    <div className="space-y-3">
                      <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--brand-border)', backgroundColor: 'var(--brand-surface)' }}>
                        <div className="flex items-start justify-between">
                          <div>
                            <h5 className="font-medium" style={{ color: 'var(--brand-text-primary)' }}>
                              John Doe
                            </h5>
                            <p className="text-sm" style={{ color: 'var(--brand-text-secondary)' }}>
                              Personal Injury Case
                            </p>
                            <div className="mt-2 flex items-center space-x-2">
                              <BrandedBadge variant="warning">MEDIUM</BrandedBadge>
                              <span className="text-sm" style={{ color: 'var(--brand-text-muted)' }}>
                                5 min wait
                              </span>
                            </div>
                          </div>
                          <BrandedButton size="small">
                            Accept
                          </BrandedButton>
                        </div>
                      </div>
                    </div>
                  </BrandedCard>

                  {/* Attorney Instructions */}
                  <BrandedCard>
                    <h4 className="font-semibold mb-2" style={{ color: 'var(--brand-primary)' }}>
                      How it works:
                    </h4>
                    <ol className="text-sm space-y-1" style={{ color: 'var(--brand-text-primary)' }}>
                      <li>1. Clients submit consultation requests</li>
                      <li>2. Requests appear in your queue</li>
                      <li>3. Click "Accept" to create a video room</li>
                      <li>4. Meet with your client via video</li>
                    </ol>
                  </BrandedCard>

                  {/* Button Examples */}
                  <div className="flex space-x-3">
                    <BrandedButton variant="primary">
                      Accept Consultation
                    </BrandedButton>
                    <BrandedButton variant="outline">
                      View Details
                    </BrandedButton>
                    <BrandedButton variant="secondary">
                      Refresh Queue
                    </BrandedButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};